import React, { useState } from 'react';
import Button from './Button';
import { consultationAPI } from '../utils/api';
import { DOG_SIZES } from '../utils/constants';
import { isValidEmail, isValidPhone } from '../utils/validators';

const ConsultationForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dogName: '',
    dogBreed: '',
    dogAge: '',
    dogSize: DOG_SIZES[0],
    message: '',
    preferredDate: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear validation error when field is changed
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Navn er påkrævet';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email er påkrævet';
    } else if (!isValidEmail(formData.email)) {
      errors.email = 'Indtast en gyldig email';
    }
    
    if (!formData.phone.trim()) {
      errors.phone = 'Telefonnummer er påkrævet';
    } else if (!isValidPhone(formData.phone)) {
      errors.phone = 'Indtast et gyldigt telefonnummer (8 cifre)';
    }
    
    if (!formData.preferredDate) {
      errors.preferredDate = 'Vælg venligst en dato';
    }
    
    if (!formData.dogName.trim()) {
      errors.dogName = 'Hundens navn er påkrævet';
    }
    
    if (!formData.dogBreed.trim()) {
      errors.dogBreed = 'Hundens race er påkrævet';
    }
    
    if (!formData.dogAge) {
      errors.dogAge = 'Hundens alder er påkrævet';
    }
    
    if (!formData.message.trim()) {
      errors.message = 'Besked er påkrævet';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Prepare the data with status field
      const consultationData = {
        ...formData,
        dogAge: parseInt(formData.dogAge, 10),
        status: 'pending', // Default status for new consultations
      };

      await consultationAPI.createConsultation(consultationData);
      setSubmitSuccess(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        dogName: '',
        dogBreed: '',
        dogAge: '',
        dogSize: DOG_SIZES[0],
        message: '',
        preferredDate: '',
      });
    } catch (error: any) {
      setSubmitError(
        error.response?.data?.message || 'Der opstod en fejl. Prøv igen senere.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <h3 className="text-2xl font-bold text-green-700 mb-4">Tak for din henvendelse!</h3>
        <p className="text-green-600 mb-6">
          Vi har modtaget din bookingforespørgsel og vil kontakte dig hurtigst muligt for at bekræfte tidspunktet.
        </p>
        <Button
          onClick={() => setSubmitSuccess(false)}
          variant="primary"
        >
          Book en ny tid
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {submitError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Personal Information */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Dit navn *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className={`w-full px-4 py-2 border ${validationErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue`}
          />
          {validationErrors.name && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className={`w-full px-4 py-2 border ${validationErrors.email ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue`}
          />
          {validationErrors.email && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Telefonnummer *
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            className={`w-full px-4 py-2 border ${validationErrors.phone ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue`}
          />
          {validationErrors.phone && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.phone}</p>
          )}
        </div>

        <div>
          <label htmlFor="preferredDate" className="block text-sm font-medium text-gray-700 mb-1">
            Foretrukken dato *
          </label>
          <input
            type="date"
            id="preferredDate"
            name="preferredDate"
            value={formData.preferredDate}
            onChange={handleChange}
            required
            min={new Date().toISOString().split('T')[0]}
            className={`w-full px-4 py-2 border ${validationErrors.preferredDate ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue`}
          />
          {validationErrors.preferredDate && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.preferredDate}</p>
          )}
        </div>

        {/* Dog Information */}
        <div>
          <label htmlFor="dogName" className="block text-sm font-medium text-gray-700 mb-1">
            Hundens navn *
          </label>
          <input
            type="text"
            id="dogName"
            name="dogName"
            value={formData.dogName}
            onChange={handleChange}
            required
            className={`w-full px-4 py-2 border ${validationErrors.dogName ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue`}
          />
          {validationErrors.dogName && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.dogName}</p>
          )}
        </div>

        <div>
          <label htmlFor="dogBreed" className="block text-sm font-medium text-gray-700 mb-1">
            Hundens race *
          </label>
          <input
            type="text"
            id="dogBreed"
            name="dogBreed"
            value={formData.dogBreed}
            onChange={handleChange}
            required
            className={`w-full px-4 py-2 border ${validationErrors.dogBreed ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue`}
          />
          {validationErrors.dogBreed && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.dogBreed}</p>
          )}
        </div>

        <div>
          <label htmlFor="dogAge" className="block text-sm font-medium text-gray-700 mb-1">
            Hundens alder (år) *
          </label>
          <input
            type="number"
            id="dogAge"
            name="dogAge"
            value={formData.dogAge}
            onChange={handleChange}
            required
            min="0"
            max="30"
            className={`w-full px-4 py-2 border ${validationErrors.dogAge ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue`}
          />
          {validationErrors.dogAge && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.dogAge}</p>
          )}
        </div>

        <div>
          <label htmlFor="dogSize" className="block text-sm font-medium text-gray-700 mb-1">
            Hundens størrelse
          </label>
          <select
            id="dogSize"
            name="dogSize"
            value={formData.dogSize}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
          >
            {DOG_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Message */}
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
          Besked *
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={4}
          className={`w-full px-4 py-2 border ${validationErrors.message ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue`}
          placeholder="Fortæl os om din hunds ernæringsmæssige udfordringer eller spørgsmål..."
        ></textarea>
        {validationErrors.message && (
          <p className="mt-1 text-sm text-red-600">{validationErrors.message}</p>
        )}
      </div>

      {/* Submit Button */}
      <div>
        <Button
          type="submit"
          variant="primary"
          fullWidth
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          Book konsultation
        </Button>
      </div>
    </form>
  );
};

export default ConsultationForm;